<template>
  <main-template>
    <v-container class="calendario-letivo-page">
      <v-row align="center" class="fade-up d1">
        <v-col cols="auto">
          <v-btn fab small class="back-btn" @click="() => $router.go(-1)">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
        </v-col>
        <v-col>
          <h1 class="page-title">Painel Administrativo — Calendário {{ anoAtual }}</h1>
          <p class="page-subtitle">
            Gerencie os eventos letivos, feriados e datas importantes do ano.
          </p>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn
            class="btn-import-premium"
            @click="abrirImportacaoFeriados"
            :disabled="!$can(['calendarioLetivo.calendariosCreate', 'calendarioLetivo.*'])"
          >
            <v-icon left>mdi-calendar-import</v-icon>
            Importar Feriados Cadastrados
          </v-btn>
        </v-col>
      </v-row>

      <v-container class="mt-5">
        <v-row>
          <v-col cols="12">
            <v-card class="panel-card fade-up d2">
              <div class="d-flex align-center px-6 pt-6 pb-2">
                <div class="card-title-badge tone-purple mr-3">
                  <v-icon small color="white">mdi-information-outline</v-icon>
                </div>
                <div>
                  <div class="card-title">Legendas</div>
                  <div class="card-subtitle">Categorias e cores dos eventos do calendário</div>
                </div>
              </div>

              <v-card-text>
                <v-row class="mt-1">
                  <v-col v-for="item in eventosList" :key="item.texto" cols="6" md="4">
                    <div class="legend-chip">
                      <span class="legend-dot" :style="{ backgroundColor: item.cor }"></span>
                      <span class="legend-label">{{ item.text }}</span>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-card class="panel-card fade-up d3 mt-5">
              <div class="d-flex align-center px-6 pt-6 pb-2">
                <div class="card-title-badge tone-blue mr-3">
                  <v-icon small color="white">mdi-calendar-month-outline</v-icon>
                </div>
                <div>
                  <div class="card-title">Eventos do Ano {{ anoAtual }}</div>
                  <div class="card-subtitle">
                    Relação completa dos eventos cadastrados no calendário letivo
                  </div>
                </div>
              </div>

              <v-card-text>
                <v-data-table
                  v-if="eventos.length"
                  :headers="headers"
                  :items="eventosOrdenados"
                  item-key="start"
                  class="custom-table"
                >
                  <template v-slot:item.start="{ item }">
                    <span class="event-date">{{ formatarData(item.start) }}</span>
                  </template>

                  <template v-slot:item.name="{ item }">
                    <div class="d-flex align-center">
                      <div
                        :style="{
                          backgroundColor: item.color,
                          width: '15px',
                          height: '15px',
                          borderRadius: '50%',
                          marginRight: '8px',
                          boxShadow: '0 0 0 3px ' + item.color + '22',
                        }"
                      ></div>
                      <span class="font-weight-medium">{{ item.name }}</span>
                    </div>
                  </template>

                  <template v-slot:item.descricao="{ item }">
                    <div class="d-flex align-center">
                      <span>{{ item.descricao }}</span>
                    </div>
                  </template>

                  <template v-slot:item.action="{ item }">
                    <v-btn
                      icon
                      small
                      color="blue"
                      @click="abrirModalEdicao(item)"
                      :disabled="
                        !$can(['calendarioLetivo.calendariosUpdate', 'calendarioLetivo.*'])
                      "
                    >
                      <v-icon>fa fa-edit</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      color="red"
                      @click="removerEvento(item)"
                      :disabled="
                        !$can(['calendarioLetivo.calendariosDelete', 'calendarioLetivo.*'])
                      "
                    >
                      <v-icon>fa fa-trash</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>

                <div v-else class="empty-state text-center">
                  <v-icon size="40" color="#c7b9e8">mdi-calendar-blank-outline</v-icon>
                  <div class="empty-state-title">Nenhum evento cadastrado</div>
                  <div class="empty-state-text">
                    Clique em um dia do calendário para criar um evento para este ano.
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-container class="mt-5">
        <v-row>
          <v-col v-for="(mes, index) in meses" :key="index" cols="12" md="6" lg="4">
            <v-card class="month-card fade-up month-anim" :style="monthDelay(index)">
              <div class="month-card-header">
                <div class="month-card-title">{{ mes }}</div>
                <div
                  class="month-card-count"
                  :class="getEventosDoMes(anoAtual, index).length ? 'month-card-count--active' : ''"
                >
                  {{ getEventosDoMes(anoAtual, index).length }} eventos
                </div>
              </div>
              <v-divider></v-divider>
              <v-card-text>
                <v-calendar
                  ref="calendar"
                  :key="mes"
                  type="month"
                  :start="getFirstDayOfMonth(index)"
                  :events="getEventosDoMes(anoAtual, index)"
                  :show-adjacent-months="false"
                  class="calendario-fixo"
                  @click:date="onDateClick"
                  @click:event="abrirModalVisualizacao"
                  event-color="color"
                >
                </v-calendar>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <e-modal-add-evento
        :modal-aberto="modalAberto && !editando"
        :novo-evento="novoEvento"
        :eventos-list="eventosList"
        :loading="loading"
        @add-evento="addEvento"
        @fechar-modal="modalAberto = false"
      ></e-modal-add-evento>

      <e-modal-edit-evento
        :modal-aberto="modalAberto && editando"
        :novo-evento="novoEvento"
        :eventos-list="eventosList"
        :loading="loading"
        @editar-evento="editarEvento"
        @fechar-modal="modalAberto = false"
      ></e-modal-edit-evento>

      <e-modal-confirmar-evento
        :modal-confirmacao-aberto="modalConfirmacaoAberto"
        :evento-selecionado="eventoSelecionado"
        :loading="loading"
        @confirmar-remocao="confirmarRemocao"
        @fechar-modal="modalConfirmacaoAberto = false"
      ></e-modal-confirmar-evento>

      <e-modal-visualizar-evento
        :modal-aberto="modalVisualizarAberto"
        :evento-selecionado="eventoSelecionado"
        @fechar-modal="modalVisualizarAberto = false"
      ></e-modal-visualizar-evento>

      <v-dialog v-model="dialogImportacaoFeriados" max-width="700" persistent>
        <v-card>
          <v-card-title class="import-dialog-header">
            <span>Importar Feriados Cadastrados</span>
            <v-chip small dark color="#4f008f">{{ anoAtual }}</v-chip>
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <div v-if="carregandoPreviewFeriados" class="text-center py-6">
              <v-progress-circular indeterminate color="primary" />
              <div class="mt-2 grey--text">Buscando feriados cadastrados...</div>
            </div>

            <div v-else-if="previewFeriadosItens.length === 0" class="text-center py-6 grey--text">
              Nenhum feriado pendente de importação encontrado para o ano de {{ anoAtual }}.
            </div>

            <template v-else>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th style="width: 48px">
                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <v-btn icon x-small v-on="on" @click="selecionarTodosFeriados">
                            <v-icon x-small>
                              {{
                                todosFeriadosSelecionados
                                  ? "mdi-check-all"
                                  : "mdi-plus-circle-multiple-outline"
                              }}
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>{{
                          todosFeriadosSelecionados ? "Desmarcar todos" : "Selecionar todos"
                        }}</span>
                      </v-tooltip>
                    </th>
                    <th>Nome do Feriado</th>
                    <th style="width: 120px">Data</th>
                    <th style="width: 100px" class="text-center">Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, idx) in previewFeriadosItens"
                    :key="idx"
                    :class="item.selecionado ? 'feriado-row--selecionado' : ''"
                  >
                    <td>
                      <v-btn
                        icon
                        x-small
                        :color="item.selecionado ? 'primary' : 'grey'"
                        @click="toggleFeriadoPreview(idx)"
                      >
                        <v-icon x-small>
                          {{ item.selecionado ? "mdi-check-circle" : "mdi-plus-circle-outline" }}
                        </v-icon>
                      </v-btn>
                    </td>
                    <td class="font-weight-medium">{{ item.nome }}</td>
                    <td class="body-2">{{ formatarDataSimples(item.data) }}</td>
                    <td class="text-center">
                      <v-chip x-small :color="corTipoFeriado(item.tipo)" dark>
                        {{ labelTipoFeriado(item.tipo) }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <div class="mt-3 caption grey--text">
                <span v-if="totalFeriadosSelecionados === 0">
                  Clique em <v-icon x-small>mdi-plus-circle-outline</v-icon> para selecionar os
                  feriados.
                </span>
                <span v-else>
                  <strong>{{ totalFeriadosSelecionados }}</strong> de
                  {{ previewFeriadosItens.length }} feriado(s) selecionado(s).
                </span>
              </div>
            </template>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text :disabled="importandoFeriados" @click="fecharImportacaoFeriados"
              >Cancelar</v-btn
            >
            <v-btn
              v-if="!carregandoPreviewFeriados && previewFeriadosItens.length > 0"
              color="primary"
              :loading="importandoFeriados"
              :disabled="totalFeriadosSelecionados === 0"
              @click="confirmarImportacaoFeriados"
            >
              Importar {{ totalFeriadosSelecionados > 0 ? `(${totalFeriadosSelecionados})` : "" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";
import EModalAddEvento from "@/components//Calendario/EModalAddEvento.vue";
import EModalEditEvento from "@/components/Calendario/EModalEditEvento.vue";
import EModalConfirmarEvento from "@/components/Calendario/EModalConfirmarEvento.vue";

export default {
  components: {
    EModalAddEvento,
    EModalEditEvento,
    EModalConfirmarEvento,
  },
  data() {
    return {
      headers: [
        { text: "Categoria", align: "start", value: "name" },
        { text: "Evento", align: "start", value: "descricao" },
        { text: "Data", align: "start", value: "start" },
        { text: "Ação", align: "center", value: "action" },
      ],
      meses: [
        "Janeiro",
        "Fevereiro",
        "Março",
        "Abril",
        "Maio",
        "Junho",
        "Julho",
        "Agosto",
        "Setembro",
        "Outubro",
        "Novembro",
        "Dezembro",
      ],
      eventosList: [],
      novoEvento: { data: null, evento_id: null, descricao: null },
      modalAberto: false,
      eventos: [],
      modalConfirmacaoAberto: false,
      eventoSelecionado: null,
      loading: false,
      editando: false,
      modalVisualizarAberto: false,
      dialogImportacaoFeriados: false,
      carregandoPreviewFeriados: false,
      previewFeriadosItens: [],
      importandoFeriados: false,
    };
  },
  mounted() {
    this.loadEventos();
    this.loadEventosCalendario();
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    anoAtual() {
      return this.user?.ano?.descricao || new Date().getFullYear();
    },
    eventosOrdenados() {
      return this.eventos.slice().sort((a, b) => new Date(a.start) - new Date(b.start));
    },
    isFormValid() {
      return this.novoEvento.tipo && this.novoEvento.descricao;
    },
    totalFeriadosSelecionados() {
      return this.previewFeriadosItens.filter((i) => i.selecionado).length;
    },
    todosFeriadosSelecionados() {
      return (
        this.previewFeriadosItens.length > 0 &&
        this.previewFeriadosItens.every((i) => i.selecionado)
      );
    },
  },
  watch: {
    anoAtual(novoAno) {
      this.carregarEventos(novoAno);
    },
  },
  methods: {
    formatarData(data) {
      const date = new Date(data);
      date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
      return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "long" });
    },

    formatarDataSimples(data) {
      if (!data) return "—";
      const [ano, mes, dia] = String(data).split("T")[0].split("-");
      return `${dia}/${mes}/${ano}`;
    },

    corTipoFeriado(tipo) {
      return { nacional: "blue", estadual: "green", municipal: "orange" }[tipo] || "grey";
    },

    labelTipoFeriado(tipo) {
      return { nacional: "Nacional", estadual: "Estadual", municipal: "Municipal" }[tipo] || tipo;
    },

    onDateClick(day) {
      const eventoExistente = this.eventos.find((e) => e.start === day.date);
      if (eventoExistente) {
        this.abrirModalVisualizacao({ event: eventoExistente });
        return;
      }

      if (this.$can(["calendarioLetivo.calendariosCreate", "calendarioLetivo.*"])) {
        this.abrirModalEvento(day);
      } else {
        this.$toast.danger("Você não tem permissão para criar eventos.");
      }
    },

    abrirModalConfirmacao(evento) {
      this.modalVisualizarAberto = false;
      this.modalAberto = false;
      this.editando = false;
      this.modalConfirmacaoAberto = false;
      this.eventoSelecionado = { ...evento, id: evento.id };
      this.modalConfirmacaoAberto = true;
    },

    async confirmarRemocao() {
      this.loading = true;
      try {
        await this.$services.calendarioLetivoService.removerEvento(this.eventoSelecionado.id);
        this.eventos = this.eventos.filter((e) => e.id !== this.eventoSelecionado.id);
        this.$toast.success("Evento removido com sucesso!");
      } catch (error) {
        this.$toast.error("Erro ao remover o evento.");
        this.$handleError(error);
      } finally {
        this.loading = false;
        this.modalConfirmacaoAberto = false;
        this.eventoSelecionado = null;
      }
    },

    async removerEvento(evento) {
      this.abrirModalConfirmacao(evento);
    },
    getFirstDayOfMonth(index) {
      return `${this.anoAtual}-${String(index + 1).padStart(2, "0")}-01`;
    },

    monthDelay(index) {
      const order = [0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.1, 0.15, 0.2, 0.25, 0.3, 0.35];
      return { animationDelay: `${order[index % order.length]}s` };
    },

    abrirModalEvento(day) {
      this.modalVisualizarAberto = false;
      this.modalConfirmacaoAberto = false;
      this.modalAberto = false;
      this.editando = false;
      this.novoEvento = { data: day.date, evento_id: null, descricao: null };
      this.modalAberto = true;
      this.editando = false;
    },

    async abrirModalEdicao(evento) {
      this.modalVisualizarAberto = false;
      this.modalConfirmacaoAberto = false;
      this.modalAberto = false;
      this.editando = false;
      await this.loadEventos();

      this.novoEvento = {
        id: evento.id,
        data: evento.start,
        evento_id: evento.evento_id,
        descricao: evento.descricao,
      };
      this.modalAberto = true;
      this.editando = true;
    },

    async addEvento() {
      const eventoExistente = this.eventos.find((e) => e.start === this.novoEvento.data);

      if (eventoExistente) {
        this.$toast.danger("Não é permitido cadastrar mais de um evento na mesma data.");
        return;
      }

      const eventoSelecionado = this.eventosList.find((e) => e.id === this.novoEvento.evento_id);
      if (!eventoSelecionado) {
        this.$toast.error("Erro ao buscar os detalhes do evento.");
        return;
      }

      if (!eventoSelecionado) {
        this.$toast.danger("Erro ao buscar os detalhes do evento.");
        return;
      }

      const eventoParaSalvar = {
        evento_id: eventoSelecionado.id,
        name: eventoSelecionado.text,
        descricao: this.novoEvento.descricao,
        start: this.novoEvento.data,
        end: this.novoEvento.data,
        color: eventoSelecionado.cor,
        ano_id: this.user?.ano?.id,
      };

      this.loading = true;
      try {
        const response = await this.$services.calendarioLetivoService.salvarCalendario(
          eventoParaSalvar
        );

        this.eventos.push({
          id: response.id,
          start: response.start,
          end: response.end,
          name: response.name,
          evento_id: response.evento_id,
          descricao: response.descricao,
          color: response.color,
          is_feriado: eventoSelecionado.is_feriado,
          is_sabado_letivo: eventoSelecionado.is_sabado_letivo,
        });

        this.$toast.success("Evento salvo com sucesso!");
      } catch (error) {
        this.$toast.error("Erro ao salvar o evento.");
        this.$handleError(error);
      } finally {
        this.loading = false;
        this.modalAberto = false;
        this.novoEvento = { data: null, evento_id: null, descricao: null };
      }
    },

    async editarEvento() {
      const eventoSelecionado = this.eventosList.find((e) => e.id === this.novoEvento.evento_id);

      if (!eventoSelecionado) {
        this.$toast.error("Erro ao buscar os detalhes do evento.");
        return;
      }

      const eventoParaSalvar = {
        id: this.novoEvento.id,
        evento_id: eventoSelecionado.id,
        name: eventoSelecionado.text,
        descricao: this.novoEvento.descricao,
        start: this.novoEvento.data,
        end: this.novoEvento.data,
        color: eventoSelecionado.cor,
        ano_id: this.user?.ano?.id,
        is_feriado: eventoSelecionado.is_feriado,
        is_sabado_letivo: eventoSelecionado.is_sabado_letivo,
      };
      this.loading = true;
      try {
        await this.$services.calendarioLetivoService.atualizarEvento(eventoParaSalvar);
        const index = this.eventos.findIndex((e) => e.id === eventoParaSalvar.id);
        if (index !== -1) {
          this.eventos.splice(index, 1, eventoParaSalvar);
        }

        this.$toast.success("Evento editado com sucesso!");
      } catch (error) {
        this.$toast.error("Erro ao editar o evento.");
        this.$handleError(error);
      } finally {
        this.loading = false;
        this.modalAberto = false;
        this.novoEvento = { data: null, evento_id: null, descricao: null };
      }
    },

    getEventosDoMes(ano, index) {
      const mes = index;

      const eventosFiltrados = this.eventos.filter((evento) => {
        const eventoData = new Date(evento.start);
        const eventoMes = eventoData.getUTCMonth();
        return eventoMes === mes;
      });

      return eventosFiltrados.map((evento) => ({
        ...evento,
        color: evento.color || "red",
      }));
    },
    async loadEventosCalendario() {
      try {
        const response = await this.$services.calendarioLetivoService.syncAll();

        this.eventos = response.map((evento) => ({
          name: evento.evento?.legenda,
          descricao: evento.descricao,
          id: evento.id,
          evento_id: evento.evento_id,
          start: evento.start,
          end: evento.end,
          color: evento.color,
          is_feriado: evento.evento?.is_feriado,
          is_sabado_letivo: evento.evento?.is_sabado_letivo,
        }));
      } catch (error) {
        this.$handleError(error);
      }
    },

    async loadEventos() {
      try {
        this.$loaderService.open("Carregando Eventos");
        const data = await this.$services.eventoCalendarioLetivoService.syncAll();

        this.eventosList = data.map((evento) => ({
          id: evento.id ?? null,
          text: evento.legenda ?? "Evento sem nome",
          value: evento.id ?? null,
          cor: evento.cor ?? null,
          is_feriado: evento.is_feriado ?? false,
          is_sabado_letivo: evento.is_sabado_letivo ?? false,
        }));
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.$loaderService.close();
      }
    },

    abrirModalVisualizacao(evento) {
      this.modalAberto = false;
      this.editando = false;
      this.modalConfirmacaoAberto = false;
      this.modalVisualizarAberto = false;
      this.eventoSelecionado = evento.event;
      this.modalVisualizarAberto = true;
    },

    async abrirImportacaoFeriados() {
      this.dialogImportacaoFeriados = true;
      this.previewFeriadosItens = [];
      this.carregandoPreviewFeriados = true;
      try {
        const anoId = this.user?.ano?.id;
        const feriados = await this.$services.calendarioLetivoService.previewImportacaoFeriados(
          anoId
        );
        this.previewFeriadosItens = feriados.map((item) => ({ ...item, selecionado: true }));
      } catch (error) {
        this.$handleError(error);
        this.dialogImportacaoFeriados = false;
      } finally {
        this.carregandoPreviewFeriados = false;
      }
    },

    fecharImportacaoFeriados() {
      this.dialogImportacaoFeriados = false;
      this.previewFeriadosItens = [];
    },

    toggleFeriadoPreview(idx) {
      this.previewFeriadosItens = this.previewFeriadosItens.map((item, i) =>
        i === idx ? { ...item, selecionado: !item.selecionado } : item
      );
    },

    selecionarTodosFeriados() {
      const novoEstado = !this.todosFeriadosSelecionados;
      this.previewFeriadosItens = this.previewFeriadosItens.map((item) => ({
        ...item,
        selecionado: novoEstado,
      }));
    },

    async confirmarImportacaoFeriados() {
      const itensSelecionados = this.previewFeriadosItens.filter((i) => i.selecionado);
      if (itensSelecionados.length === 0) return;
      try {
        this.importandoFeriados = true;
        const resultado = await this.$services.calendarioLetivoService.importarFeriados({
          ano_id: this.user?.ano?.id,
          itens: itensSelecionados.map((i) => ({ nome: i.nome, data: i.data })),
        });
        const total = resultado.importados;
        const msg =
          total === 1
            ? "1 feriado importado com sucesso para o calendário!"
            : `${total} feriados importados com sucesso para o calendário!`;
        this.$toast.success(msg);
        this.fecharImportacaoFeriados();
        await this.loadEventosCalendario();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.importandoFeriados = false;
      }
    },
  },
};
</script>

<style scoped>
/* ===== TOKENS LOCAIS (claro / escuro) ===== */
.calendario-letivo-page {
  --cl-brand-border-forte: #d0bbf3;
  --cl-brand-border-hover: #b49be8;
}

.theme--dark .calendario-letivo-page {
  --cl-brand-border-forte: rgba(167, 139, 250, 0.35);
  --cl-brand-border-hover: rgba(167, 139, 250, 0.6);
}

/* ===== CABEÇALHO ===== */
.import-dialog-header {
  padding: 16px 20px;
  background: linear-gradient(135deg, var(--brand-soft), var(--surface));
  border-bottom: 1px solid var(--brand-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 800;
}

.page-title {
  font-size: 21px;
  font-weight: 800;
  color: var(--text);
  line-height: 1.2;
  margin: 0;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-muted);
  font-weight: 500;
  margin-top: 2px;
}

.back-btn {
  background: var(--brand-soft) !important;
  color: var(--brand) !important;
  border-radius: 11px;
  box-shadow: 0 4px 10px rgba(79, 0, 143, 0.12);
  transition: background-color 0.18s ease, transform 0.18s ease !important;
}

.back-btn:hover {
  background: var(--brand-border) !important;
  transform: scale(1.05);
}

.btn-import-premium {
  color: var(--brand) !important;
  font-weight: 700;
  border: 1px solid var(--cl-brand-border-forte) !important;
  background: var(--input-bg) !important;
  border-radius: 11px !important;
  transition: all 0.18s ease !important;
}

.btn-import-premium:not(.v-btn--disabled):hover {
  transform: translateY(-2px);
  background: var(--brand-soft-2) !important;
  border-color: var(--cl-brand-border-hover) !important;
  box-shadow: 0 8px 20px rgba(79, 0, 143, 0.18);
}

/* ===== CARDS DE PAINEL ===== */
.panel-card {
  position: relative;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.card-title {
  font-size: 15px;
  font-weight: 800;
  color: var(--text);
}

.card-subtitle {
  font-size: 12px;
  color: var(--text-faint);
  margin-top: 2px;
}

.card-title-badge {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 5px 12px rgba(15, 23, 42, 0.12);
}

.card-title-badge.tone-purple {
  background: linear-gradient(135deg, #7c3aed, #a78bfa);
}

.card-title-badge.tone-blue {
  background: linear-gradient(135deg, #0284c7, #38bdf8);
}

/* ===== IMPORTAÇÃO DE FERIADOS ===== */
.feriado-row--selecionado {
  background: var(--brand-soft) !important;
}

/* ===== LEGENDAS ===== */
.legend-chip {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: var(--surface-2);
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  transition: transform 0.18s ease, border-color 0.18s ease, background-color 0.18s ease;
}

.legend-chip:hover {
  transform: translateY(-2px);
  background: var(--surface-hover);
  border-color: var(--cl-brand-border-forte);
}

.legend-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  margin-right: 10px;
  flex-shrink: 0;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06);
}

.legend-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-3);
}

/* ===== TABELA ===== */
.custom-table ::v-deep .v-data-table-header th {
  font-weight: 700;
  color: var(--text-3);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  background: var(--surface-hover);
}

.custom-table ::v-deep tbody tr:hover {
  background: var(--surface-hover) !important;
}

.event-date {
  font-weight: 600;
  color: var(--brand);
}

/* ===== ESTADO VAZIO ===== */
.empty-state {
  padding: 36px 0 28px;
}

.empty-state-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-2);
  margin-top: 10px;
}

.empty-state-text {
  font-size: 13px;
  color: var(--text-faint);
  margin-top: 4px;
}

/* ===== MESES ===== */
.month-card {
  position: relative;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  overflow: hidden;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.month-card:hover {
  transform: translateY(-3px);
  border-color: rgba(124, 58, 237, 0.35);
  box-shadow: 0 14px 30px rgba(79, 0, 143, 0.12);
}

.month-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
}

.month-card-title {
  font-size: 15px;
  font-weight: 800;
  color: var(--text-2);
}

.month-card-count {
  font-size: 11px;
  font-weight: 700;
  color: var(--text-faint);
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--surface-3);
}

.month-card-count.month-card-count--active {
  color: var(--brand);
  background: var(--brand-soft-2);
}

.calendario-fixo {
  min-height: 400px;
  max-height: 400px;
  overflow: hidden;
}

/* ===== ANIMAÇÃO ===== */
.fade-up {
  animation: fadeUp 0.5s ease both;
}

.d1 {
  animation-delay: 0.05s;
}

.d2 {
  animation-delay: 0.1s;
}

.d3 {
  animation-delay: 0.18s;
}

.month-anim {
  animation: fadeUp 0.5s ease both;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .fade-up,
  .month-anim {
    animation: none;
  }
}
</style>
